'use client';

import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { CircleIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

const RadioColorContext = React.createContext<{
  background?: string;
  foreground?: string;
}>({});

function RadioGroup({
  className,
  background,
  foreground,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root> & {
  background?: string;
  foreground?: string;
}) {
  return (
    <RadioColorContext.Provider value={{ background, foreground }}>
      <RadioGroupPrimitive.Root
        data-slot="radio-group"
        className={cn('grid gap-3', className)}
        {...props}
      />
    </RadioColorContext.Provider>
  );
}

function RadioGroupItem({
  className,
  background,
  foreground,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item> & {
  background?: string;
  foreground?: string;
}) {
  const groupColors = React.useContext(RadioColorContext);
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={cn(
        'border-input cursor-pointer text-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 aspect-square size-4 shrink-0 rounded-full border shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      style={{ backgroundColor: background ?? groupColors.background }}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="relative flex items-center justify-center"
      >
        <CircleIcon
          className="fill-current absolute top-1/2 left-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2"
          data-slot="radio-group-icon"
          style={{ color: foreground ?? groupColors.foreground }}
        />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
}

export { RadioGroup, RadioGroupItem };
